'use client';

import {ExternalSSP, YaMobileFloorBanner, YandexFloorAd} from '@shared/Ads';
import {safeLocalStorage} from '@utils/safeStorages';
import {usePathname} from 'next/navigation';
import {useEffect, useState} from 'react';
import {UAParser} from 'ua-parser-js';
import {v4} from 'uuid';

import {TEmitYmEvent} from '@shared/Ads/Yandex/types';

import {logTmrEventAdapter} from '@web-landing/analytics/helpers/logTmrEvent';
import {emitYmEvent} from '@web-landing/analytics/helpers/ymEvent';

import {useOmicron} from '../OmicronClientContext';
import {ADSEnvs} from './types';

const SSP_USER_ID = 'sspUserId';

const getADSConfingByPlacement = (
  pathName: string,
  config: Record<string, string>,
) => {
  if (pathName.includes('/prostore')) return config?.['blog'] || 'none';
  if (pathName.includes('/instruction'))
    return config?.['instruction'] || 'none';

  return config?.['main'] || 'none';
};

const proxyYmEvent: TEmitYmEvent = ({eventName, params}) => {
  emitYmEvent(eventName, params);
};

export const MultiProviderFloorAd = (envs: ADSEnvs) => {
  const omicron = useOmicron();
  const {
    ADFOX_CONFIG,
    LANDING_SSP_AD_MODE,
    LANDING_SSP_AD_PARTNER,
    LANDING_YANDEX_AD_MODE,
    SSP_BANNER_REFRESH,
  } = omicron?.config ?? {};
  const pathName = usePathname();
  const SSPMode = getADSConfingByPlacement(pathName, LANDING_SSP_AD_MODE);
  const yandexMode = getADSConfingByPlacement(pathName, LANDING_YANDEX_AD_MODE);
  const [isSSpShown, setIsSSPShown] = useState(true);
  const [deviceType, setDeviceType] = useState<
    UAParser.IDevice['type'] | 'unknown'
  >('unknown');

  useEffect(() => {
    const parser = new UAParser(navigator.userAgent);
    setDeviceType(parser.getDevice()?.type);

    const sspUserId = safeLocalStorage.getItem(SSP_USER_ID) || v4();

    if (!safeLocalStorage.getItem(SSP_USER_ID)) {
      safeLocalStorage.setItem(SSP_USER_ID, sspUserId);
    }

    try {
      fetch(`https://ad.mail.ru/cm.gif?p=156&id=${sspUserId}`, {
        mode: 'no-cors',
      }).catch(console.warn);
    } catch (err) {
      console.warn('Ad pixel fetch failed', err);
    }
  }, []);

  const anySspEnabled =
    SSPMode === 'ssp_floor_ad' || SSPMode === 'ssp_floor_ad_closed';

  if (anySspEnabled && isSSpShown) {
    const slotId =
      LANDING_SSP_AD_PARTNER === 'ssp_floor_ad_partner' ? '9' : '1';

    return (
      <ExternalSSP
        setIsSSPShown={setIsSSPShown}
        slotId={slotId}
        logTmrEvent={logTmrEventAdapter}
        emitYmEvent={proxyYmEvent}
        SSP_SCRIPT_URL={envs.SSP_SCRIPT_URL}
        SSP_URL={envs.SSP_URL}
        omicron={{
          sspTimerEnable: SSP_BANNER_REFRESH.sspTimerEnable,
          sspRefreshOnNavigate: SSP_BANNER_REFRESH.sspRefreshOnNavigate,
          sspTimerDuration: SSP_BANNER_REFRESH.sspTimerDuration,
          sspRefreshOnFocusTab: SSP_BANNER_REFRESH.sspRefreshOnFocusTab,
        }}
      />
    );
  }

  if (
    yandexMode === 'floor_ad_banner' ||
    yandexMode === 'floor_ad_banner_scroll'
  ) {
    if (deviceType === 'unknown') {
      return null;
    }
    return deviceType === 'mobile' ? (
      <YaMobileFloorBanner
        adFoxConfig={ADFOX_CONFIG}
        logTmrEvent={logTmrEventAdapter}
        emitYmEvent={proxyYmEvent}
        blockId={
          yandexMode === 'floor_ad_banner'
            ? envs.YANDEX_ADS_FLOOR_AD_BANNER_ID
            : envs.YANDEX_ADS_FLOOR_AD_BANNER_SCROLL_ID
        }
        appearMode={
          yandexMode === 'floor_ad_banner' ? 'on_mount' : 'after_scroll'
        }
      />
    ) : (
      <YandexFloorAd
        mode="floor_ad_closed"
        env={envs}
        analytics={{
          emitYmEvent: proxyYmEvent,
          logTmrEvent: logTmrEventAdapter,
        }}
        config={ADFOX_CONFIG}
      />
    );
  }

  if (
    yandexMode === 'floor_ad' ||
    yandexMode === 'floor_ad_closed' ||
    yandexMode === 'floor_ad_closed_copy'
  ) {
    return (
      <YandexFloorAd
        mode={yandexMode}
        env={envs}
        analytics={{
          emitYmEvent: proxyYmEvent,
          logTmrEvent: logTmrEventAdapter,
        }}
        config={ADFOX_CONFIG}
      />
    );
  }

  return null;
};
